import { IEnv } from '../../../infrastructure/i-env';
import { Language } from '../../../dto/language';
import { TranslationMode } from '../../../dto/translation-mode';
import { IUserPreferencesService } from '../../../services/i-user-preferences-service';
import type { EnabledLanguagePairDto, UserPreferencesDto } from '../../../dto/user-preferences-dto';

export interface LanguageOption {
	code: Language;
	name: string;
}

export interface LearningSettings {
	nativeLanguage: Language | null;
	learnedLanguage: Language | null;
	mode: TranslationMode | null;
}

export interface MySettingsState {
	selectedNativeLanguage: Language | null;
	selectedLearnLanguage: Language | null;
	selectedLevel: 'beginner' | 'intermediate' | null;
	selectedDailyWordCountTarget: number;

	originalNativeLanguage: Language | null;
	originalLearnLanguage: Language | null;
	originalLevel: 'beginner' | 'intermediate' | null;
	originalDailyWordCountTarget: number;

	saveSuccess: boolean;
	hasChanges: boolean;

	nativeLanguages: LanguageOption[];
	allLearnLanguages: LanguageOption[];
	learnLanguages: LanguageOption[];
}

const DEFAULT_LANGUAGE_OPTIONS: LanguageOption[] = [
	{ code: Language.EN, name: 'English' },
	{ code: Language.FR, name: 'French' },
	{ code: Language.DE, name: 'German' },
	{ code: Language.ES, name: 'Spanish' },
	{ code: Language.IT, name: 'Italian' },
	{ code: Language.RU, name: 'Russian' },
	{ code: Language.HY, name: 'Armenian' },
	{ code: Language.HI, name: 'Hindi' },
	{ code: Language.BN, name: 'Bengali' }
];
const DEFAULT_DAILY_WORD_COUNT_TARGET = 10;
const DAILY_WORD_TARGET_OPTIONS = [5, 10, 20] as const;

/**
 * Abstract view model for my settings page.
 * Framework-agnostic view model that manages learning preferences.
 */
export abstract class IMySettingsViewModel {
	constructor(protected readonly env: IEnv) {}

	abstract readonly state: MySettingsState;

	abstract loadPreferences(): Promise<void>;
	abstract handleSave(): Promise<void>;
	abstract resetChanges(): void;
	abstract getLanguageName(code: Language | null): string;
}

/**
 * Framework-agnostic my settings view model.
 * Manages learning preferences state and logic.
 */
export class MySettingsViewModel extends IMySettingsViewModel {
	public readonly state: MySettingsState;
	private readonly storageKey = 'lingvo-news-preferences';
	private enabledLanguagePairs: readonly EnabledLanguagePairDto[] = [];

	constructor(
		env: IEnv
	) {
		super(env);

		this.state = env.reactive({
			selectedNativeLanguage: null,
			selectedLearnLanguage: null,
			selectedLevel: null,
			selectedDailyWordCountTarget: DEFAULT_DAILY_WORD_COUNT_TARGET,
			originalNativeLanguage: null,
			originalLearnLanguage: null,
			originalLevel: null,
			originalDailyWordCountTarget: DEFAULT_DAILY_WORD_COUNT_TARGET,
			saveSuccess: false,
			hasChanges: false,
			nativeLanguages: [...DEFAULT_LANGUAGE_OPTIONS],
			allLearnLanguages: [...DEFAULT_LANGUAGE_OPTIONS],
			learnLanguages: []
		}) as MySettingsState;

		this.recomputeDerived();


		env.watch(
			() => this.state.selectedNativeLanguage,
			(newNativeLang) => {
				if (newNativeLang && this.state.selectedLearnLanguage === newNativeLang) {
					this.state.selectedLearnLanguage = null;
				}
				this.recomputeDerived();
			}
		);

		env.watchMultiple(
			[
				() => this.state.selectedNativeLanguage,
				() => this.state.selectedLearnLanguage,
				() => this.state.selectedLevel,
				() => this.state.selectedDailyWordCountTarget,
				() => this.state.originalNativeLanguage,
				() => this.state.originalLearnLanguage,
				() => this.state.originalLevel,
				() => this.state.originalDailyWordCountTarget
			] as const,
			() => this.recomputeDerived()
		);
	}

	async loadPreferences(): Promise<void> {
		await this.loadEnabledLanguagePairs();
		const userId = this.env.currentUser.getUserId();
		if (this.env.currentUser.isAuthenticated() && userId) {
			try {
				const userService = this.env.get(IUserPreferencesService);
				const remote = await userService.getUserPreferences(userId);
				if (remote) {
					this.applyDto(remote);
					this.env.clientStorage.set(this.storageKey, remote);
					return;
				}
			} catch (error) {
				console.error('Error loading preferences from backend:', error);
			}
		}

		this.loadFromClientStorage();
		this.recomputeDerived();
	}

	async handleSave(): Promise<void> {
		if (!this.state.selectedNativeLanguage || !this.state.selectedLearnLanguage || !this.state.selectedLevel) {
			return;
		}

		const mode: TranslationMode = this.state.selectedLevel === 'beginner' ? TranslationMode.Partial : TranslationMode.Full;

		const existing = this.env.clientStorage.get<UserPreferencesDto>(this.storageKey);
		const interests = existing?.interests ?? '';
		const dateTimeOffset = new Date().getTimezoneOffset();

		const dto: UserPreferencesDto = {
			nativeLanguage: this.state.selectedNativeLanguage,
			learnedLanguage: this.state.selectedLearnLanguage,
			mode,
			interests,
			dailyWordCountTarget: this.state.selectedDailyWordCountTarget,
			dateTimeOffset,
			timestamp: Date.now()
		};

		this.env.clientStorage.set(this.storageKey, dto);

		const userId = this.env.currentUser.getUserId();
		if (this.env.currentUser.isAuthenticated() && userId) {
			try {
				const userService = this.env.get(IUserPreferencesService);
				await userService.setUserPreferences(userId, dto);
			} catch (error) {
				console.error('Error saving settings to backend:', error);
			}
		}

		this.state.saveSuccess = true;
		this.storeOriginals();
		this.recomputeDerived();

		// Navigate to home/news list using the selected preferences (never fall back to defaults).
		await this.env.routing.navigate(
				this.env.routing.getNewsListLink(dto.nativeLanguage, dto.learnedLanguage, mode)
		);

		setTimeout(() => {
			this.state.saveSuccess = false;
			this.recomputeDerived();
		}, 3000);
	}

	resetChanges(): void {
		this.state.selectedNativeLanguage = this.state.originalNativeLanguage;
		this.state.selectedLearnLanguage = this.state.originalLearnLanguage;
		this.state.selectedLevel = this.state.originalLevel;
		this.state.selectedDailyWordCountTarget = this.state.originalDailyWordCountTarget;
		this.state.saveSuccess = false;
		this.recomputeDerived();
	}

	getLanguageName(code: Language | null): string {
		if (!code) return '';
		const allLanguages = [...this.state.nativeLanguages, ...this.state.allLearnLanguages];
		const lang = allLanguages.find((l) => l.code === code);
		return lang ? lang.name : code;
	}

	private loadFromClientStorage(): void {
		const saved = this.env.clientStorage.get<UserPreferencesDto>(this.storageKey);
		if (!saved) {
			this.clearSelections();
			return;
		}

		// Note: we intentionally tolerate partial / legacy shapes in storage.
		const dto = saved as unknown;
		if (dto && typeof dto === 'object') {
			const r = dto as Record<string, unknown>;
			const nativeLanguage = typeof r.nativeLanguage === 'string' ? (r.nativeLanguage as Language) : null;
			const learnedLanguage = typeof r.learnedLanguage === 'string' ? (r.learnedLanguage as Language) : null;
			const mode = typeof r.mode === 'string' ? (r.mode as TranslationMode) : null;
			const dailyWordCountTarget = this.normalizeDailyWordCountTarget(r.dailyWordCountTarget);

			this.state.selectedNativeLanguage = nativeLanguage;
			this.state.selectedLearnLanguage = learnedLanguage;
			this.state.selectedDailyWordCountTarget = dailyWordCountTarget;
			if (mode === TranslationMode.Partial) {
				this.state.selectedLevel = 'beginner';
			} else if (mode === TranslationMode.Full) {
				this.state.selectedLevel = 'intermediate';
			} else {
				this.state.selectedLevel = null;
			}

			this.storeOriginals();
			this.recomputeDerived();
			return;
		}

		this.clearSelections();
	}

	private applyDto(dto: UserPreferencesDto): void {
		this.state.selectedNativeLanguage = dto.nativeLanguage ?? null;
		this.state.selectedLearnLanguage = dto.learnedLanguage ?? null;
		this.state.selectedDailyWordCountTarget = this.normalizeDailyWordCountTarget(dto.dailyWordCountTarget);
		if (dto.mode === TranslationMode.Partial) {
			this.state.selectedLevel = 'beginner';
		} else if (dto.mode === TranslationMode.Full) {
			this.state.selectedLevel = 'intermediate';
		} else {
			this.state.selectedLevel = null;
		}

		this.storeOriginals();
		this.recomputeDerived();
	}

	private clearSelections(): void {
		this.state.selectedNativeLanguage = null;
		this.state.selectedLearnLanguage = null;
		this.state.selectedLevel = null;
		this.state.selectedDailyWordCountTarget = DEFAULT_DAILY_WORD_COUNT_TARGET;
		this.storeOriginals();
		this.recomputeDerived();
	}

	private storeOriginals(): void {
		this.state.originalNativeLanguage = this.state.selectedNativeLanguage;
		this.state.originalLearnLanguage = this.state.selectedLearnLanguage;
		this.state.originalLevel = this.state.selectedLevel;
		this.state.originalDailyWordCountTarget = this.state.selectedDailyWordCountTarget;
	}

	private recomputeDerived(): void {
		const preferredMode = this.state.selectedLevel === 'intermediate'
			? TranslationMode.Full
			: TranslationMode.Partial;

		let effectiveMode = preferredMode;
		let pairsForMode = this.enabledLanguagePairs.filter((pair) => pair.mode === effectiveMode);

		// If selected mode has no enabled pairs, switch to any available mode to keep selectors usable.
		if (this.enabledLanguagePairs.length > 0 && pairsForMode.length === 0) {
			effectiveMode = this.enabledLanguagePairs[0].mode;
			pairsForMode = this.enabledLanguagePairs.filter((pair) => pair.mode === effectiveMode);
			const fallbackLevel = effectiveMode === TranslationMode.Full ? 'intermediate' : 'beginner';
			if (this.state.selectedLevel !== fallbackLevel) {
				this.state.selectedLevel = fallbackLevel;
			}
		}

		if (pairsForMode.length > 0) {
			const nativeCodes = effectiveMode === TranslationMode.Full
				? pairsForMode.map((pair) => pair.target)
				: pairsForMode.map((pair) => pair.source);
			const allLearnCodes = effectiveMode === TranslationMode.Full
				? pairsForMode.map((pair) => pair.source)
				: pairsForMode.map((pair) => pair.target);

			this.state.nativeLanguages = this.toLanguageOptions(nativeCodes);
			this.state.allLearnLanguages = this.toLanguageOptions(allLearnCodes);

			if (this.state.selectedNativeLanguage && !nativeCodes.includes(this.state.selectedNativeLanguage)) {
				this.state.selectedNativeLanguage = null;
			}

			if (!this.state.selectedNativeLanguage) {
				this.state.learnLanguages = [];
				if (this.state.selectedLearnLanguage) {
					this.state.selectedLearnLanguage = null;
				}
			} else {
				const learnCodes = effectiveMode === TranslationMode.Full
					? pairsForMode
						.filter((pair) => pair.target === this.state.selectedNativeLanguage)
						.map((pair) => pair.source)
					: pairsForMode
						.filter((pair) => pair.source === this.state.selectedNativeLanguage)
						.map((pair) => pair.target);
				this.state.learnLanguages = this.toLanguageOptions(learnCodes);
				if (this.state.selectedLearnLanguage && !learnCodes.includes(this.state.selectedLearnLanguage)) {
					this.state.selectedLearnLanguage = null;
				}
			}
		} else {
			// Fallback for environments where enabled pairs are not configured.
			this.state.nativeLanguages = [...DEFAULT_LANGUAGE_OPTIONS];
			this.state.allLearnLanguages = [...DEFAULT_LANGUAGE_OPTIONS];
			this.state.learnLanguages = this.state.allLearnLanguages.filter((lang) => lang.code !== this.state.selectedNativeLanguage);
			if (this.state.selectedLearnLanguage && !this.state.learnLanguages.some((l) => l.code === this.state.selectedLearnLanguage)) {
				this.state.selectedLearnLanguage = null;
			}
		}

		this.state.hasChanges =
			this.state.selectedNativeLanguage !== this.state.originalNativeLanguage ||
			this.state.selectedLearnLanguage !== this.state.originalLearnLanguage ||
			this.state.selectedLevel !== this.state.originalLevel ||
			this.state.selectedDailyWordCountTarget !== this.state.originalDailyWordCountTarget;
	}

	private normalizeDailyWordCountTarget(value: unknown): number {
		if (typeof value === 'number' && DAILY_WORD_TARGET_OPTIONS.includes(value as 5 | 10 | 20)) {
			return value;
		}
		return DEFAULT_DAILY_WORD_COUNT_TARGET;
	}

	private toLanguageOptions(codes: readonly Language[]): LanguageOption[] {
		const defaultNameByCode = new Map(DEFAULT_LANGUAGE_OPTIONS.map((option) => [option.code, option.name] as const));
		const seen = new Set<Language>();
		const result: LanguageOption[] = [];
		for (const code of codes) {
			if (seen.has(code)) {
				continue;
			}
			seen.add(code);
			result.push({
				code,
				name: defaultNameByCode.get(code) ?? code
			});
		}
		return result;
	}

	private async loadEnabledLanguagePairs(): Promise<void> {
		try {
			const userService = this.env.get(IUserPreferencesService);
			this.enabledLanguagePairs = await userService.getEnabledLanguagePairs();
		} catch (error) {
			console.error('Error loading enabled language pairs:', error);
			this.enabledLanguagePairs = [];
		}
		this.recomputeDerived();
	}

	public format(): string {
		return [
			'MySettingsViewModel',
			`selectedNativeLanguage: ${this.state.selectedNativeLanguage ?? ''}`,
			`selectedLearnLanguage: ${this.state.selectedLearnLanguage ?? ''}`,
			`selectedLevel: ${this.state.selectedLevel ?? ''}`,
			`originalNativeLanguage: ${this.state.originalNativeLanguage ?? ''}`,
			`originalLearnLanguage: ${this.state.originalLearnLanguage ?? ''}`,
			`originalLevel: ${this.state.originalLevel ?? ''}`,
			`saveSuccess: ${this.state.saveSuccess}`,
			`hasChanges: ${this.state.hasChanges}`,
			`learnLanguagesCount: ${this.state.learnLanguages.length}`
		].join('\n');
	}
}
